<template>
    <div class="form-step">
        <!-- Consecutive short questions share a row on desktop (3 per row);
             everything else takes the full width. -->
        <div
            v-for="block in blocks"
            :key="block.key"
            :class="block.short ? 'question-row' : 'question-block'"
        >
            <form-question
                ref="questions"
                v-for="question in block.questions"
                :key="question.id"
                :name="question.id"
                :question="question.question"
                :type="question.type"
                :options="question.options"
                :settings="question.settings"
                :multiselect="question.multiselect"
                :form-id="formId"
                :link-code="linkCode"
                @focus="focus"
                @change="questionChanged"
            >
            </form-question>
        </div>
    </div>
</template>
<script>
import FormQuestion from "./form-question";
import { isQuestionVisible } from "../utils/visibilityConditionEvaluator";
import viewport from "../utils/viewport";

function isShortQuestion(question) {
    if (question.type === "input" || question.type === "date") return true;
    return question.type === "selectbox" && !question.multiselect;
}

export default {
    name: "step",
    props: {
        questions: Array,
        formId: [String, Number],
    },
    components: {
        "form-question": FormQuestion,
    },
    computed: {
        answers() {
            return this.$store.state.answers;
        },
        linkCode() {
            const linkCode = this.$store.state.linkCode;
            return linkCode ? linkCode.link_code : null;
        },
        visibleQuestions() {
            if (!this.questions) return [];
            return this.questions.filter(question =>
                isQuestionVisible(question, this.answers)
            );
        },
        // Runs of short questions (one-line text, date, single-choice
        // dropdown) are grouped so the desktop layout can put them side by
        // side. Every other question is a block of its own.
        blocks() {
            const blocks = [];
            this.visibleQuestions.forEach((question) => {
                const short = isShortQuestion(question);
                const last = blocks[blocks.length - 1];
                if (short && last && last.short) {
                    last.questions.push(question);
                    return;
                }
                blocks.push({ key: question.id, short, questions: [question] });
            });
            return blocks;
        },
    },
    data: function () {
        return {
            firstInvalid: null,
        };
    },
    watch: {
        answers: {
            deep: true,
            handler: function () {
                this.pruneHiddenAnswers();
            },
        },
    },
    methods: {
        pruneHiddenAnswers: function () {
            const workingAnswers = Object.assign({}, this.answers);
            (this.questions || []).forEach((question) => {
                if (
                    !isQuestionVisible(question, workingAnswers) &&
                    Object.prototype.hasOwnProperty.call(workingAnswers, question.id)
                ) {
                    delete workingAnswers[question.id];
                    this.$store.commit("removeAnswer", question.id);
                }
            });
        },
        isValid: function () {
            window.mixpanel?.track("Form step is valid");
            var valid = true;
            this.firstInvalid = null;
            // $refs in a v-for are not guaranteed to follow the page order.
            const questions = (this.$refs.questions || [])
                .slice()
                .sort((a, b) => a.getOffsetTop() - b.getOffsetTop());
            for (let q in questions) {
                var question = questions[q];
                if (!question.isValid()) {
                    valid = false;
                    if (!this.firstInvalid) {
                        this.firstInvalid = question;
                    }
                }
            }
            return valid;
        },
        firstInvalidElement: function () {
            return this.firstInvalid ? this.firstInvalid.invalidElement() : null;
        },
        questionChanged: function (question) {
            question.isValid(true);
            this.$store.commit("setAnswer", {
                questionId: question.name,
                value: question.getValue()
            });
            this.followOnPhone(question);
        },
        focus: function (question) {
            this.followOnPhone(question);
        },
        // On a phone the page follows the question being answered. On a
        // desktop everything is in view and jumping around gets in the way.
        followOnPhone: function (question) {
            if (viewport.desktop) {
                return;
            }
            this.$emit("scroll", question.getOffsetTop());
        },
    },
};
</script>
